/**
 * Density conversion utilities for smart tank monitoring.
 *
 * Reference:
 *   Water density at 20°C ≈ 998.2067 kg/m³ (= g/L)
 *
 * Brix and Oechsle are both measures of sugar concentration in liquids
 * (commonly wine/must), derived from specific gravity.
 */

import React from "react";
import { FunctionField } from "react-admin";
import { useFieldValue } from "ra-core";
import { custInfo } from "./customer";

// Reference density of water at 20°C [kg/m³ = g/L]
export const RHO_WATER = 998.2067;

// ---------------------------------------------------------------------------
// Brix conversions
// ---------------------------------------------------------------------------

/**
 * Convert a density (g/L or kg/m³) to degrees Brix.
 *
 * @param density - density in g/L (numerically equivalent to kg/m³)
 */
export function densityToBrix(density: number | null): number | null {
  if (density === null) return null;
  const sg = density / RHO_WATER;
  return 261.54 * (1 - 1 / sg);
}

/**
 * Convert degrees Brix to density (g/L or kg/m³).
 *
 * @param brix - degrees Brix
 */
export function brixToDensity(brix: number | null): number | null {
  if (brix === null) return null;
  const sg = 1 / (1 - brix / 261.54);
  return sg * RHO_WATER;
}

// ---------------------------------------------------------------------------
// Oechsle conversions
// ---------------------------------------------------------------------------

/**
 * Convert a density (g/L or kg/m³) to degrees Oechsle.
 *
 * @param density - density in g/L (numerically equivalent to kg/m³)
 */
export function densityToOechsle(density: number | null): number | null {
  if (density === null) return null;
  const sg = density / RHO_WATER;
  return (sg - 1.0) * 1000.0;
}

/**
 * Convert degrees Oechsle to density (g/L or kg/m³).
 *
 * @param oechsle - degrees Oechsle
 */
export function oechsleToDensity(oechsle: number | null): number | null {
  if (oechsle === null) return null;
  const sg = oechsle / 1000.0 + 1.0;
  return sg * RHO_WATER;
}

/**
 * Display a density value converted to the customer's preferred unit.
 *
 * The stored `startingDens` field is always in SI units (kg/m³).
 * This component converts it to °Bx or °Oe based on
 * `custInfo.customerPreferences.densityUnit`.
 *
 * Supports nested source paths (e.g. "data.startingDens") using the same
 * `lodash/get` logic as React Admin's built-in fields.
 */
const DensityField = ({
  source = "startingDens",
  label,
}: {
  source?: string;
  label?: string;
}) => {
  const rawValue = useFieldValue({ source });
  if (rawValue == null) return <FunctionField render={() => "\u00A0"} />;

  const value = Number(rawValue);
  if (!Number.isFinite(value)) return <FunctionField render={() => "\u00A0"} />;

  const unit = custInfo.customerPreferences.densityUnit;
  let renderText: string;

  if (unit === "g/L") {
    renderText = `${value.toFixed(0)} g/L`;
  } else if (unit === "°Oe") {
    renderText = `${densityToOechsle(value)?.toFixed(1) ?? "\u00A0"} °Oe`;
  } else {
    renderText = `${densityToBrix(value)?.toFixed(1) ?? "\u00A0"} °Bx`;
  }

  return <FunctionField render={() => renderText} label={label} />;
};

export { DensityField };
